import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Download, Sparkles, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import WidgetRenderer from "@/components/WidgetRenderer";

export default function DatasetView() {
  const { id, slug } = useParams();
  const nav = useNavigate();
  const [ds, setDs] = useState(null);
  const [tab, setTab] = useState("overview");
  const [dashCreating, setDashCreating] = useState(false);

  useEffect(() => {
    api.get(`/datasets/${id}`).then(({ data }) => setDs(data)).catch(() => { toast.error("Failed"); nav(`/t/${slug}/datasets`); });
  }, [id]);

  const download = async (fmt) => {
    try {
      const res = await api.get(`/datasets/${id}/download?format=${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = `${ds.name}.${fmt}`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const buildAutoDashboard = async () => {
    setDashCreating(true);
    try {
      const { data } = await api.post(`/datasets/${id}/auto-dashboard`);
      toast.success("Dashboard created");
      nav(`/t/${slug}/dashboards/${data.id}`);
    } catch { toast.error("Failed"); }
    finally { setDashCreating(false); }
  };

  if (!ds) return <div className="p-10 text-slate-400">Loading…</div>;

  const q = ds.quality || {};
  const semantic = ds.semantic || {};

  return (
    <div className="max-w-7xl mx-auto p-6 lg:p-10">
      <Link to={`/t/${slug}/datasets`} className="link-quiet inline-flex items-center gap-2 text-sm mb-6" data-testid="back-datasets">
        <ArrowLeft size={14} /> Back to datasets
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="chip">{ds.kind?.replace("_", " ") || ds.category}</span>
            <span className="chip">{ds.filename}</span>
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-medium tracking-tight text-slate-900" data-testid="dataset-title">{ds.name}</h1>
          {ds.description && <p className="text-slate-500 mt-2 max-w-2xl">{ds.description}</p>}
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={buildAutoDashboard} disabled={dashCreating} data-testid="build-auto" className="btn-primary">
            {dashCreating ? <><Loader2 size={14} className="animate-spin" /> Building…</> : <><Sparkles size={14} /> Auto-dashboard</>}
          </button>
          <button onClick={() => download("csv")} data-testid="dl-csv" className="btn-ghost"><Download size={14} /> CSV</button>
          <button onClick={() => download("xlsx")} data-testid="dl-xlsx" className="btn-ghost"><Download size={14} /> XLSX</button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Kpi label="Rows" value={ds.row_count?.toLocaleString()} />
        <Kpi label="Columns" value={ds.columns.length} />
        <Kpi label="Data Quality" value={`${q.quality_score ?? "—"}`} sub="/ 100" />
        <Kpi label="Duplicates" value={q.duplicate_employee_ids ?? 0} tone={q.duplicate_employee_ids ? "warning" : "success"} />
      </div>

      <div className="border-b border-[var(--border)] flex gap-1 mb-6 overflow-x-auto">
        {[
          { k: "overview", l: "Data Intelligence" },
          { k: "schema", l: "Columns & Semantics" },
          { k: "data", l: "Preview" },
        ].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`tab-${t.k}`}
            className={`px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
              tab === t.k ? "border-[var(--primary)] text-[var(--primary)]" : "border-transparent text-slate-500 hover:text-slate-900"
            }`}>{t.l}</button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 card">
              <div className="label-caps mb-3">Auto-detected structure</div>
              <div className="space-y-3">
                <DetectionRow label="Employee identifier" value={ds.detected?.employee_id} />
                <DetectionRow label="Manager (hierarchy)" value={ds.detected?.manager_id} />
                <DetectionRow label="Date columns" value={(ds.detected?.dates || []).join(", ")} />
                <DetectionRow label="Dimensions" value={(ds.detected?.dimensions || []).slice(0, 8).join(", ")} />
                <DetectionRow label="Measures" value={(ds.detected?.measures || []).join(", ")} />
                <DetectionRow label="Organizational hierarchy" value={ds.detected?.has_hierarchy ? "Yes — reporting lines available" : "Not detected"} success={ds.detected?.has_hierarchy} />
              </div>
            </div>
            <div className="card">
              <div className="label-caps mb-3">Data quality</div>
              <div className="kpi-value text-[var(--primary)]">{q.quality_score ?? "—"}</div>
              <div className="text-sm text-slate-500 mt-2">Overall quality score</div>
              <div className="divider-h my-4" />
              <QualityRow label="Completeness" value={`${q.completeness_pct ?? "—"}%`} tone={q.completeness_pct >= 95 ? "success" : "warning"} />
              <QualityRow label="Duplicate IDs" value={q.duplicate_employee_ids ?? 0} tone={q.duplicate_employee_ids ? "warning" : "success"} />
              <QualityRow label="Total rows" value={q.total_rows?.toLocaleString() || "—"} />
            </div>
          </div>

          <div className="card">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-[var(--primary-soft)] flex items-center justify-center">
                <Sparkles size={16} className="text-[var(--primary)]" />
              </div>
              <div>
                <div className="font-medium text-slate-900">Ready to explore?</div>
                <div className="text-sm text-slate-500">Generate a full workforce dashboard for this dataset, or ask a question.</div>
              </div>
              <div className="ml-auto flex gap-2">
                <button onClick={buildAutoDashboard} disabled={dashCreating} className="btn-primary" data-testid="build-auto-2">
                  <Sparkles size={14} /> Auto-dashboard
                </button>
                <Link to={`/t/${slug}/ask`} className="btn-ghost">Ask PeopleIQ</Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === "schema" && (
        <div className="card-flush overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-[var(--border)]">
              <tr>
                <th className="text-left px-5 py-3 label-caps font-medium">Column</th>
                <th className="text-left px-5 py-3 label-caps font-medium">Type</th>
                <th className="text-left px-5 py-3 label-caps font-medium">Detected as</th>
                <th className="text-left px-5 py-3 label-caps font-medium">Missing</th>
              </tr>
            </thead>
            <tbody>
              {ds.columns.map((c) => (
                <tr key={c} className="border-b border-slate-100">
                  <td className="px-5 py-3 mono text-slate-900">{c}</td>
                  <td className="px-5 py-3"><span className="chip">{ds.column_types[c]}</span></td>
                  <td className="px-5 py-3"><span className="chip chip-primary">{semantic[c] || "—"}</span></td>
                  <td className="px-5 py-3 mono text-xs text-slate-500">{q.missing_by_column?.[c] || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "data" && (
        <div className="card-flush">
          <div className="px-5 py-3 border-b border-[var(--border)]">
            <div className="label-caps">Preview · first 100 rows</div>
          </div>
          <div className="overflow-auto scroll-thin max-h-[600px]">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-slate-50 border-b border-[var(--border)]">
                <tr>
                  {ds.columns.map((c) => <th key={c} className="text-left px-4 py-2.5 label-caps font-medium">{c}</th>)}
                </tr>
              </thead>
              <tbody>
                {ds.preview.map((row, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    {ds.columns.map((c) => (
                      <td key={c} className={`px-4 py-2 ${row[c] == null ? "text-slate-400" : "text-slate-800"}`}>
                        {row[c] == null ? "—" : String(row[c])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, sub, tone = "neutral" }) {
  const toneCls = { success: "text-emerald-600", warning: "text-amber-600", danger: "text-red-600", neutral: "text-slate-900" }[tone];
  return (
    <div className="card">
      <div className="label-caps mb-3">{label}</div>
      <div className={`kpi-value ${toneCls}`}>{value}{sub && <span className="text-lg font-normal text-slate-400 ml-1">{sub}</span>}</div>
    </div>
  );
}
function DetectionRow({ label, value, success }) {
  return (
    <div className="flex items-start gap-3 py-2 border-b border-slate-100 last:border-0">
      <div className="mt-0.5">{success ? <CheckCircle2 size={14} className="text-emerald-500" /> : value ? <CheckCircle2 size={14} className="text-[var(--primary)]" /> : <AlertTriangle size={14} className="text-slate-300" />}</div>
      <div className="flex-1 min-w-0">
        <div className="text-xs text-slate-500 uppercase tracking-widest">{label}</div>
        <div className="text-sm text-slate-800 mono truncate">{value || "—"}</div>
      </div>
    </div>
  );
}
function QualityRow({ label, value, tone = "neutral" }) {
  const cls = { success: "chip-success", warning: "chip-warning", neutral: "chip" }[tone];
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className="text-sm text-slate-600">{label}</span>
      <span className={`chip ${cls === "chip" ? "" : cls}`}>{value}</span>
    </div>
  );
}
